import React, { useEffect, useRef } from "react";

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * An accessible modal (WAI-ARIA dialog pattern): `role="dialog"` +
 * `aria-modal`, named by `labelledBy`; focus moves in on open (to the element
 * marked `data-autofocus`, else the first control), Tab and Shift+Tab stay
 * inside it, Escape cancels, and focus goes back to what opened it on close.
 * The page behind is hidden from assistive technology by the screen (it sets
 * `aria-hidden` on its own content while a dialog is open).
 */
export default function Dialog({ labelledBy, onClose, children, testId }) {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const previous = document.activeElement;
    const controls = () => [...node.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
    const first = node.querySelector("[data-autofocus]") || controls()[0] || node;
    first.focus();

    function onKeyDown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const list = controls();
      if (!list.length) {
        event.preventDefault();
        node.focus();
        return;
      }
      const head = list[0];
      const tail = list[list.length - 1];
      const active = document.activeElement;
      if (!node.contains(active)) {
        event.preventDefault();
        head.focus();
      } else if (event.shiftKey && (active === head || active === node)) {
        event.preventDefault();
        tail.focus();
      } else if (!event.shiftKey && active === tail) {
        event.preventDefault();
        head.focus();
      }
    }
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      if (previous && typeof previous.focus === "function" && document.contains(previous)) previous.focus();
    };
  }, []);

  return (
    <div className="wr-overlay">
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={labelledBy} tabIndex={-1} className="wr-dialog" data-testid={testId}>
        {children}
      </div>
    </div>
  );
}
